import { Pie, Rose } from "@ant-design/charts";
import { gql, useQuery } from "@apollo/client";
import { Card, Col, Layout, PageHeader, Row, Typography } from "antd";
import Title from "antd/lib/typography/Title";
import { useApplicationContext } from "components/Application";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import React, { Fragment } from "react";

const topItemsQuery = gql`
  query ($kind: String!) {
    topItemsForKind(kind: $kind) {
      item {
        id
        nameEn
        shortDescriptionEn
        cover
        color {
          code
          nameEn
          patch
        }
        season {
          id
          nameEn
        }
      }
      quantity
    }
  }
`;

const topMaterialsQuery = gql`
  query {
    topMaterials {
      material {
        id
        code
        nameEn
        descriptionEn
      }
      quantity
    }
  }
`;

const topBuyersQuery = gql`
  query ($salespersonId: ID) {
    topBuyers(salespersonId: $salespersonId) {
      user {
        id
        code
        email
        companyName
        role
      }
      quantity
    }
  }
`;
const topSalespersonsQuery = gql`
  query {
    topSalespersons {
      user {
        id
        code
        email
        companyName
        role
      }
      quantity
    }
  }
`;

export const Dash: React.FC<{}> = () => {
  const {
    // siteEnvironment: { appEndpointStore },
    session,
  } = useApplicationContext();

  const salespersonId = session.currentUser.role === "SALESPERSON" ? session.currentUser.id : null;

  const {
    error: manError,
    loading: manLoading,
    data: manData,
  } = useQuery(topItemsQuery, {
    variables: { kind: "Man" },
    fetchPolicy: "cache-and-network",
  });

  const {
    error: womanError,
    loading: womanLoading,
    data: womanData,
  } = useQuery(topItemsQuery, {
    variables: { kind: "Woman" },
    fetchPolicy: "cache-and-network",
  });

  const {
    error: materialError,
    loading: materialLoading,
    data: materialData,
  } = useQuery(topMaterialsQuery, {
    fetchPolicy: "cache-and-network",
  });

  const {
    error: buyerError,
    loading: buyerLoading,
    data: buyerData,
  } = useQuery(topBuyersQuery, {
    variables: { salespersonId },
    fetchPolicy: "cache-and-network",
  });

  const {
    error: salesError,
    loading: salesLoading,
    data: salesData,
  } = useQuery(topSalespersonsQuery, {
    fetchPolicy: "cache-and-network",
  });

  if (manLoading || womanLoading || materialLoading || buyerLoading || salesLoading)
    return <CenterSpinner size="large" />;
  if (manError || womanError || materialError || buyerError || salesError)
    return <CenterResult status="404" title="404" subTitle="Sorry, the page you visited does not exist." />;

  const manTopItems = manData.topItemsForKind;
  const womanTopItems = womanData.topItemsForKind;
  const topMaterials = materialData.topMaterials;
  const topBuyers = buyerData.topBuyers;
  const topSalespersons = salesData.topSalespersons;

  const renderPieChart = (items: any[], title: string) => {
    const PieChart: React.FC = () => {
      const renderPieData = (items: any[]) => {
        return items.map((top: any, i: any) => {
          if (title === "COMPOSITIONS") {
            const { material } = top;
            return {
              type: `${material.code} - ${material.nameEn}`,
              value: top.quantity,
            };
          } else if (title === "MAN" || title === "WOMAN") {
            const { item } = top;
            return {
              type: `${item.nameEn} - ${item.color.code}`,
              value: top.quantity,
            };
          } else {
            const { user } = top;
            let type = `${user.code} - ${user.companyName}`;
            if (user.role === "SALESPERSON") {
              type = `${user.code} - ${user.email}`;
            }
            return {
              type: type,
              value: top.quantity,
            };
          }
        });
      };

      const pieData = renderPieData(items);

      var pieConfig = {
        appendPadding: 5,
        data: pieData,
        angleField: "value",
        colorField: "type",
        radius: 0.9,
        innerRadius: 0.65,
        label: {
          type: "inner",
          offset: "-50%",
          content: function content(_ref: any) {
            var percent = _ref.percent;
            return "".concat((percent * 100).toFixed(1), "%");
          },
          style: {
            textAlign: "center",
            fontSize: 14,
          },
        },
        interactions: [{ type: "element-selected" }, { type: "element-active" }],
        tooltip: {
          customContent: (title: any, data: any) => {
            return `<div style="padding: 5px"><div style="width: 8px; height: 8px; border-radius: 8px; float: left; margin-right: 7px; margin-top: 2px; background-color:${data[0]?.color}"></div>${title}</div>`;
          },
        },
        statistic: {
          title: false,
          content: {
            style: {
              whiteSpace: "pre-wrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            formatter: function formatter() {
              return title;
            },
          },
        },
      };
      return <Pie {...pieConfig} />;
    };

    return <PieChart />;
  };

  const renderRoseChart = (items: any[], title: string) => {
    const RoseChart: React.FC = () => {
      const renderRoseData = (items: any[]) => {
        return items.map((top: any, i: any) => {
          if (title === "COMPOSITIONS") {
            const { material } = top;
            return {
              type: `${material.code} - ${material.nameEn}`,
              value: top.quantity,
            };
          } else if (title === "MAN" || title === "WOMAN") {
            const { item } = top;
            return {
              type: `${item.nameEn} - ${item.color.code}`,
              value: top.quantity,
            };
          } else {
            const { user } = top;
            let type = `${user.code} - ${user.companyName}`;
            if (user.role === "SALESPERSON") {
              type = `${user.code} - ${user.email}`;
            }
            return {
              type: type,
              value: top.quantity,
            };
          }
        });
      };

      const roseData = renderRoseData(items);

      var roseConfig = {
        data: roseData,
        xField: "type",
        yField: "value",
        seriesField: "type",
        radius: 0.9,
        label: {
          offset: -1000,
        },
        tooltip: {
          customContent: (title: any, data: any) => {
            return `<div style="padding: 5px"><div style="width: 8px; height: 8px; border-radius: 8px; float: left; margin-right: 7px; margin-top: 2px; background-color:${data[0]?.color}"></div>${title}</div>`;
          },
        },
      };
      return <Rose {...roseConfig} />;
    };

    return <RoseChart />;
  };

  const renderCard = (title: any, children: any) => (
    <Card bordered>
      <Row gutter={[5, 5]}>
        <Col>
          <Title level={5}>{title}</Title>
        </Col>
      </Row>
      {children}
    </Card>
  );

  // const renderItems = (items: any[]) => {
  //   return items.map((top: any, i: any) => {
  //     const { item } = top;
  //     return (
  //       <Row gutter={[5, 5]} key={i}>
  //         {i === 0 && (
  //           <Fragment>
  //             <Col span={4}>Cover</Col>
  //             <Col span={8}>Item</Col>
  //             <Col span={8}>Color</Col>
  //             <Col span={4}>Qty Sold</Col>
  //           </Fragment>
  //         )}
  //         <Col span={4}>
  //           <Image src={`${appEndpointStore}${item.cover}`} width={30} />
  //         </Col>
  //         <Col span={8}>
  //           <b>{item.nameEn}</b>
  //         </Col>
  //         <Col span={8}>
  //           <Image src={`${appEndpointStore}${item.color.patch}`} width={20} height={20} /> {item.color.code} -{" "}
  //           {item.color.nameEn}
  //         </Col>
  //         <Col span={4}>
  //           <b>{top.quantity}</b>
  //         </Col>
  //       </Row>
  //     );
  //   });
  // };

  const renderUsers = (users: any[]) => {
    return users.map((top: any, i: any) => {
      const { user } = top;
      let color = "#1890ff";
      if (i === 0) {
        color = "#F6BD16";
      }
      return (
        <Row gutter={[5, 5]} key={i}>
          {i === 0 && (
            <Fragment>
              <Col span={2}></Col>
              <Col span={6}>Code</Col>
              <Col span={16}>
                {user?.role === "SALESPERSON" && "Email"}
                {user?.role === "USER" && "Company Name"}
              </Col>
            </Fragment>
          )}
          <Col span={2} style={{ textAlign: "center" }}>
            <Typography.Title style={{ color: color }} level={5}>
              {i + 1}
            </Typography.Title>
          </Col>
          <Col span={6}>
            <Typography.Title style={i === 0 && { color: color }} level={5}>
              {user?.code}
            </Typography.Title>
          </Col>
          <Col span={16}>
            <Typography.Title style={i === 0 && { color: color }} level={5}>
              {user?.role === "SALESPERSON" && user?.email}
              {user?.role === "USER" && user?.companyName}
            </Typography.Title>
          </Col>
        </Row>
      );
    });
  };

  return (
    <Layout className="content">
      <PageHeader title="Stats" />
      <Layout.Content className="content">
        <Row gutter={[20, 20]}>
          <Col span={14}>{renderCard("BEST SELLERS", renderPieChart(manTopItems, "MAN"))}</Col>
          <Col span={10}>{renderCard("BEST SELLERS MAN", renderRoseChart(manTopItems, "MAN"))}</Col>
          <Col span={14}>{renderCard("BEST SELLERS", renderPieChart(womanTopItems, "WOMAN"))}</Col>
          <Col span={10}>{renderCard("BEST SELLERS WOMAN", renderRoseChart(womanTopItems, "WOMAN"))}</Col>
          <Col span={14}>{renderCard("BEST SELLERS", renderPieChart(topMaterials, "COMPOSITIONS"))}</Col>
          <Col span={10}>{renderCard("BEST SELLERS COMPOSITIONS", renderRoseChart(topMaterials, "COMPOSITIONS"))}</Col>
          {session.currentUser.role === "SALESPERSON" && (
            <Col span={24}>{renderCard("BEST BUYERS", renderUsers(topBuyers))}</Col>
          )}
          {session.currentUser.role === "ADMIN" && (
            <>
              <Col span={12}>{renderCard("BEST BUYERS", renderUsers(topBuyers))}</Col>
              <Col span={12}>{renderCard("BEST SALESPERSONS", renderUsers(topSalespersons))}</Col>
              <Col span={14}>{renderCard("TOP 10", renderPieChart(topBuyers, "BUYERS"))}</Col>
              <Col span={10}>{renderCard("BEST BUYERS", renderRoseChart(topBuyers, "BUYERS"))}</Col>
              <Col span={14}>{renderCard("TOP 10", renderPieChart(topSalespersons, "SALESPERSONS"))}</Col>
              <Col span={10}>{renderCard("BEST SALESPERSONS", renderRoseChart(topSalespersons, "SALESPERSONS"))}</Col>
              {/* <Col span={12}>{renderCard("BEST MAN ITEMS", renderItems(manTopItems))}</Col>
              <Col span={12}>{renderCard("BEST WOMAN ITEMS", renderItems(womanTopItems))}</Col> */}
            </>
          )}
        </Row>
      </Layout.Content>
    </Layout>
  );
};
